import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";

import { SiteLayout } from "@/components/SiteLayout";
import { USE_CASES } from "@/lib/cases-content";

const TITLE = "Casos de uso do TrajetoFácil — viagem, trabalho e empresas";
const DESCRIPTION =
  "Como usar o TrajetoFácil em situações reais: orçamento de viagem pessoal, cobrança de deslocamento profissional e controle de custos em empresas e frotas.";
const URL = "https://www.trajetofacil.com.br/casos-de-uso";

export const Route = createFileRoute("/casos-de-uso/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: URL }],
  }),
  component: UseCasesIndexPage,
});

function UseCasesIndexPage() {
  return (
    <SiteLayout>
      <header className="max-w-2xl">
        <h1 className="text-3xl font-extrabold tracking-tight text-foreground">Casos de uso</h1>
        <p className="mt-3 text-base leading-7 text-muted-foreground">
          O mesmo cálculo — distância, combustível, pedágio e custo por km — resolve problemas bem
          diferentes. Veja o roteiro completo de cada situação, com exemplos numéricos.
        </p>
      </header>

      <ul className="mt-10 space-y-4">
        {USE_CASES.map((c) => (
          <li key={c.slug}>
            <Link
              to="/casos-de-uso/$slug"
              params={{ slug: c.slug }}
              className="group block rounded-2xl border border-border bg-card p-5 transition hover:border-primary/50 hover:shadow-sm"
            >
              <h2 className="text-lg font-bold text-foreground group-hover:text-primary">{c.title}</h2>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">{c.short}</p>
              <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary">
                Ver o roteiro <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
              </span>
            </Link>
          </li>
        ))}
      </ul>

      <section className="mt-10 space-y-3 text-base leading-7 text-foreground">
        <h2 className="text-xl font-bold">O que forma o custo de uma viagem</h2>
        <p>
          Em todos os cenários, o valor final nasce das mesmas parcelas: a distância percorrida, o
          consumo e o preço do combustível, as praças de pedágio do trajeto e a categoria do veículo. A
          diferença entre um caso e outro está no que se faz com o número — orçar, cobrar ou controlar.
        </p>
        <p>
          Para entender cada parcela, veja a{" "}
          <Link to="/metodologia" className="font-medium text-primary underline underline-offset-4">
            metodologia
          </Link>{" "}
          e os{" "}
          <Link to="/guias" className="font-medium text-primary underline underline-offset-4">
            guias
          </Link>
          .
        </p>
      </section>
    </SiteLayout>
  );
}
